// Setzt die OSD-Sprache des TV auf die vom Gast gewaehlte App-Sprache.
//
// Warum das noetig ist: die Oberflaechen, die NICHT von uns kommen - Google
// Cast, AirPlay, Bildschirm-/Tonspiegelung - richten sich nach der OSD-Sprache
// des Geraets, nicht nach unserer App. Ohne diesen Abgleich waehlt der Gast in
// unserem Menue Franzoesisch und bekommt die Cast-Oberflaeche trotzdem in der
// Sprache, in der der TV ausgeliefert wurde.
// Quelle: Samsung Tizen Partner Summit 2026, "TEP Web API's - Settings":
// webapis.settings.setItemValue("osd-language", "fr_FR.utf8").
//
// Nur Samsung. Auf LG und im Browser passiert nichts.

import DeviceType from '@/helpers/DeviceType';

const MENU_ID = 'osd-language';

// App-Sprache -> Tizen-Locale. Die Schweizer Varianten teilen sich die Locale
// mit der jeweiligen Hauptsprache; der TV kennt kein ch_de.
//
// ACHTUNG: welche Locale-Strings eine Firmware akzeptiert, ist nicht
// dokumentiert. setItemValue liefert einen Boolean zurueck, und
// getOsdLanguage() liest zurueck, was tatsaechlich gesetzt wurde - beides
// landet in der Diagnose auf /info, damit auf echter Hardware sichtbar ist,
// welche Werte durchgehen.
export const OSD_LOCALE = {
  en: 'en_GB.utf8',
  de: 'de_DE.utf8',
  ch_de: 'de_DE.utf8',
  fr: 'fr_FR.utf8',
  ch_fr: 'fr_FR.utf8',
  it: 'it_IT.utf8',
  ch_it: 'it_IT.utf8',
  es: 'es_ES.utf8',
  tr: 'tr_TR.utf8',
  ru: 'ru_RU.utf8',
  nl: 'nl_NL.utf8',
  ar: 'ar_AE.utf8',
  ko: 'ko_KR.utf8',
  el: 'el_GR.utf8',
  pt: 'pt_PT.utf8',
  hu: 'hu_HU.utf8',
  da: 'da_DK.utf8',
  sv: 'sv_SE.utf8',
};

// Welche OSD-Sprache der Gast bekommt. Bewusst nur zwei: Deutsch fuer de und
// ch_de, alles andere Englisch. Die Cast-/AirPlay-Oberflaechen und die
// OTT-Apps sind in beiden sicher vorhanden, und jeder Wechsel stellt die
// Menuesprache des ganzen TV um - je weniger Ziele, desto seltener passiert das.
export function guestOsdLang(appLang) {
  return appLang === 'de' || appLang === 'ch_de' ? 'de' : 'en';
}

// Ersatz zum LESEN, wenn webapis.settings die Menuesprache nicht herausgibt.
//
// Am U800F (Tizen 9) wirft getItemValue('osd-language') firmwareintern
// ("Cannot read properties of undefined (reading 'itemdata')") - der Punkt
// fehlt dort in der Liste der freigegebenen Menue-Ids, das Setzen ist aber
// weiter angeboten. tizen.systeminfo LOCALE ist Standard-Tizen (ohne
// Privileg) und liefert die Systemsprache des Geraets, also die OSD-Sprache.
//
// Die API antwortet nur asynchron, get() ist synchron: der Wert wird beim
// Laden einmal geholt und per Listener nachgefuehrt. Er laeuft damit einem
// setItemValue HINTERHER und taugt nicht zum Rueckvergleich direkt nach dem
// Setzen (siehe apply).
let systemLocale = null;

// 'en_GB', 'en-GB', 'en_GB.UTF-8' -> 'en_GB.utf8' (Schreibweise von OSD_LOCALE).
export function normalizeLocale(raw) {
  const m = /^([a-z]{2,3})[_-]([a-z]{2})/i.exec(String(raw || ''));
  return m ? m[1].toLowerCase() + '_' + m[2].toUpperCase() + '.utf8' : null;
}

// Die zuletzt gemeldete Systemsprache ('de_DE.utf8') oder null - fuer die
// Einstellungs-Tabelle (DeviceSettings/Tizen.js).
export function systemLocaleValue() {
  return systemLocale;
}

function rememberLocale(locale) {
  const value = normalizeLocale(locale && locale.language);
  if (value) systemLocale = value;
}

(function watchSystemLocale() {
  try {
    const si = typeof window !== 'undefined' && window.tizen && window.tizen.systeminfo;
    if (!si || typeof si.getPropertyValue !== 'function') return;
    si.getPropertyValue('LOCALE', rememberLocale, () => {});
    if (typeof si.addPropertyValueChangeListener === 'function') {
      si.addPropertyValueChangeListener('LOCALE', rememberLocale);
    }
  } catch (e) {
    // ohne Ersatz - get() meldet dann wie bisher null
  }
})();

function settings() {
  if (typeof window === 'undefined') return undefined;
  const w = window.webapis;
  return w && w.settings ? w.settings : undefined;
}

export default class OsdLanguage {
  // Aktuelle OSD-Sprache des TV. Liefert { value, osdName, via } bzw. null.
  // via: 'settings' (getItemValue, sofort aktuell) oder 'locale'
  // (tizen.systeminfo LOCALE, Ersatz - siehe oben).
  static get() {
    const item = OsdLanguage.readSetting();
    if (item) return item;
    return systemLocale ? { value: systemLocale, osdName: '', via: 'locale' } : null;
  }

  static readSetting() {
    const s = settings();
    if (!s || typeof s.getItemValue !== 'function') return null;
    try {
      const item = s.getItemValue(MENU_ID);
      if (!item) return null;
      // Je nach Firmware kommt ein Objekt oder direkt der Wert zurueck.
      if (typeof item === 'string') return { value: item, osdName: '', via: 'settings' };
      return { value: item.value || '', osdName: item.osdName || '', via: 'settings' };
    } catch (e) {
      console.log('[OSD] getItemValue fehlgeschlagen:', e && e.message);
      return null;
    }
  }

  // Setzt die OSD-Sprache fuer die App-Sprache des Gastes (siehe guestOsdLang).
  static applyGuest(appLang) {
    const result = OsdLanguage.apply(guestOsdLang(appLang));
    result.appLang = appLang || '';
    return result;
  }

  // Setzt die OSD-Sprache passend zur App-Sprache. Liefert das Ergebnis als
  // Objekt, damit die Diagnose zeigen kann, was versucht wurde und was
  // herauskam - ein blosses true/false waere hier zu wenig.
  static apply(langKey) {
    const result = { supported: false, lang: langKey || '', locale: '', ok: false, detail: '' };
    if (!DeviceType.isTizen) {
      result.detail = 'nur auf Samsung Tizen';
      return result;
    }
    const s = settings();
    if (!s || typeof s.setItemValue !== 'function') {
      result.detail = 'webapis.settings.setItemValue nicht vorhanden';
      return result;
    }
    result.supported = true;

    const locale = OSD_LOCALE[langKey];
    if (!locale) {
      result.detail = 'keine Locale fuer "' + langKey + '" hinterlegt';
      return result;
    }
    result.locale = locale;

    // Schon richtig gesetzt? Dann nichts anfassen - der Aufruf laeuft bei
    // jedem Sprachwechsel und soll den TV nicht unnoetig umkonfigurieren.
    // Nur ueber getItemValue: der LOCALE-Ersatz ist dafuer nicht sicher genug.
    const current = OsdLanguage.readSetting();
    if (current && current.value === locale) {
      result.ok = true;
      result.detail = 'bereits gesetzt';
      return result;
    }

    try {
      const written = s.setItemValue(MENU_ID, locale);
      // Zurueckvergleichen statt dem Rueckgabewert zu vertrauen: manche
      // Firmwares melden true und uebernehmen den Wert trotzdem nicht.
      const after = OsdLanguage.readSetting();
      if (!after) {
        // Kein Rueckvergleich moeglich (U800F): der LOCALE-Ersatz zieht erst
        // nach. Dann zaehlt der Rueckgabewert - und die Diagnose sagt, dass
        // es ungeprueft ist.
        result.ok = written !== false;
        result.detail =
          'setItemValue lieferte ' + written + ' - Rueckvergleich auf diesem Geraet nicht moeglich';
        console.log('[OSD] ' + langKey + ' -> ' + locale + ': ' + result.detail);
        return result;
      }
      result.ok = after.value === locale;
      result.detail = result.ok
        ? 'gesetzt auf ' + locale
        : 'setItemValue lieferte ' + written + ', Geraet meldet ' + ((after && after.value) || 'nichts');
      console.log('[OSD] ' + langKey + ' -> ' + locale + ': ' + result.detail);
      return result;
    } catch (e) {
      result.detail = (e && (e.message || e.name)) || String(e);
      console.log('[OSD] setItemValue fehlgeschlagen:', result.detail);
      return result;
    }
  }
}



// WEBPACK FOOTER //
// ./src/helpers/OsdLanguage.js